import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { MdCategory } from "react-icons/md";

const COLORS = [
  "#dc2626", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6",
  "#ec4899", "#06b6d4", "#84cc16", "#ef4444", "#6366f1",
  "#14b8a6", "#d946ef", "#f97316", "#22c55e", "#eab308",
];

const DetailedDirectCostVm = ({ data, loading }) => {
  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const getChartData = () => {
    if (!data || data.length === 0) return [];
    const total = data.reduce((sum, item) => sum + item.totalCost, 0);
    return data.map((item) => ({
      name: item.serviceName,
      value: item.totalCost,
      percentage: (item.totalCost / total) * 100,
    }));
  };

  const chartData = getChartData();

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-gray-300 text-sm mb-1">{dataPoint.name}</p>
          <p className="text-primary-cyan text-lg font-bold">
            {formatAmount(dataPoint.value)}
          </p>
          <p className="text-gray-400 text-xs">
            Percentage: {dataPoint.percentage.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-primary-dark rounded-xl p-6 border border-gray-700/50">
        <div className="text-center py-8">
          <div className="inline-block w-8 h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-3">Loading service costs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-primary-dark rounded-xl p-6  border border-gray-700/50">
      <div className="flex items-center gap-2 mb-6 pb-3 border-b border-gray-700">
        <MdCategory className="text-primary-cyan text-2xl" />
        <h3 className="text-xl font-semibold text-white">Direct Cost by Service</h3>
      </div>

      {chartData.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-400">No service cost data available</p>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                animationDuration={1000}
                label={({ name, percent }) =>
                  `${(percent * 100).toFixed(0)}%`
                }
                labelLine={{ stroke: "#9CA3AF", strokeWidth: 1 }}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 flex flex-wrap justify-center gap-3 max-h-32 overflow-y-auto">
            {chartData.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                />
                <span className="text-gray-300 text-xs truncate max-w-[150px]">
                  {item.name.length > 30 ? item.name.substring(0, 27) + "..." : item.name}
                </span>
                <span className="text-gray-500 text-xs">
                  ({item.percentage.toFixed(1)}%)
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default DetailedDirectCostVm;